import React, { useState, useEffect } from "react";
import { useParams } from "react-router-dom";
import { API } from "../App";
import { Button } from "./ui/button";
import { Label } from "./ui/label";
import { Card, CardHeader, CardTitle, CardContent } from "./ui/card";
import { toast } from "sonner";
import { KeyRound, Loader2, CheckCircle2, AlertTriangle } from "lucide-react";
import { PasswordInput } from "./PasswordInput";

const goToLogin = (clearSession) => {
  if (clearSession) localStorage.removeItem("token");
  window.location.href = "/login";
};

export default function ResetPassword() {
  const { token } = useParams();
  const [state, setState] = useState("checking"); // checking | ready | invalid | done
  const [email, setEmail] = useState("");
  const [pwd, setPwd] = useState({ a: "", b: "" });
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    fetch(`${API}/auth/reset-password/${token}`)
      .then(async (r) => {
        if (!r.ok) return setState("invalid");
        const d = await r.json();
        setEmail(d.email || "");
        setState("ready");
      })
      .catch(() => setState("invalid"));
  }, [token]);

  const submit = async (e) => {
    e.preventDefault();
    if (pwd.a.length < 6) return toast.error("Password must be at least 6 characters");
    if (pwd.a !== pwd.b) return toast.error("Passwords do not match");
    setSaving(true);
    try {
      const r = await fetch(`${API}/auth/reset-password`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ token, new_password: pwd.a }),
      });
      const d = await r.json();
      if (r.ok) setState("done");
      else if (r.status === 400 && /invalid|expired/i.test(d.detail || "")) setState("invalid");
      else toast.error(typeof d.detail === "string" ? d.detail : "Could not reset password");
    } catch {
      toast.error("Could not reset password. Please try again.");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-slate-50 via-amber-50 to-orange-50 px-4">
      <Card className="w-full max-w-md glass-morphism border-amber-200 shadow-xl" data-testid="reset-password-page">
        <CardHeader className="text-center pb-2">
          <div className="mx-auto w-12 h-12 rounded-full bg-amber-100 flex items-center justify-center mb-2">
            <KeyRound className="w-6 h-6 text-amber-700" />
          </div>
          <CardTitle className="text-xl text-slate-900">Reset your password</CardTitle>
          {email && state === "ready" && <p className="text-sm text-slate-500" data-testid="reset-password-email">{email}</p>}
        </CardHeader>
        <CardContent>
          {state === "checking" && (
            <div className="flex justify-center py-6"><Loader2 className="w-6 h-6 animate-spin text-amber-600" /></div>
          )}
          {state === "invalid" && (
            <div className="text-center space-y-4" data-testid="reset-password-invalid">
              <AlertTriangle className="w-10 h-10 text-amber-600 mx-auto" />
              <p className="text-sm text-slate-600">This reset link is invalid or has expired. Links last 30 minutes and can only be used once.</p>
              <Button onClick={() => goToLogin(false)} className="w-full btn-construction" data-testid="reset-password-back-login">
                Back to Sign In
              </Button>
            </div>
          )}
          {state === "done" && (
            <div className="text-center space-y-4" data-testid="reset-password-success">
              <CheckCircle2 className="w-10 h-10 text-green-600 mx-auto" />
              <p className="text-sm text-slate-600">Your password has been reset and all other sessions have been signed out.</p>
              <Button onClick={() => goToLogin(true)} className="w-full btn-construction" data-testid="reset-password-go-login">
                Sign In
              </Button>
            </div>
          )}
          {state === "ready" && (
            <form onSubmit={submit} className="space-y-4" data-testid="reset-password-form">
              <div className="space-y-2">
                <Label htmlFor="reset-new">New Password</Label>
                <PasswordInput id="reset-new" placeholder="At least 6 characters" value={pwd.a} required
                  onChange={(e) => setPwd({ ...pwd, a: e.target.value })}
                  className="border-amber-200 focus:border-amber-400" data-testid="reset-new-password-input" />
              </div>
              <div className="space-y-2">
                <Label htmlFor="reset-confirm">Confirm Password</Label>
                <PasswordInput id="reset-confirm" placeholder="Re-enter your password" value={pwd.b} required
                  onChange={(e) => setPwd({ ...pwd, b: e.target.value })}
                  className="border-amber-200 focus:border-amber-400" data-testid="reset-confirm-password-input" />
              </div>
              <Button type="submit" disabled={saving} className="w-full btn-construction h-11 font-semibold" data-testid="reset-password-submit-button">
                {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : "Reset Password"}
              </Button>
            </form>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
